import { pageContent } from './content/pages';
import { productMenu, securityMenu, solutionsMenu } from './content/site';
import { articles } from './content/articles';
import { getArticleAuthor } from './content/article-authors';
import { blogCategories } from './content/blog-categories';
import { adCampaigns } from './content/get-started';

export type StaticPage = {
  path: string;
  canonicalPath: string;
  title: string;
  description: string;
  socialImage?: {
    path: string;
    alt: string;
    width: number;
    height: number;
    type: string;
  };
  noIndex?: boolean;
  blogCategory?: string;
  article?: {
    headline: string;
    datePublished: string;
    dateModified: string;
    authorName: string;
    authorImage: string;
    category: string;
    example: boolean;
    image?: string;
    imageAlt?: string;
  };
};

export const redirects = [
  {
    paths: ['/hiring', '/jobs', '/careers'],
    to: 'https://onchainden.notion.site/Join-Den-Catalyze-The-Economy-s-Transition-To-Web3-65762ed68b0246c3aa6893eb87231626',
  },
  { paths: ['/hiring/apply'], to: 'https://airtable.com/shrxLwrYY04Qzs5XJ' },
  {
    paths: ['/hiring/reimbursements'],
    to: 'https://airtable.com/shr2WpBOVFQtuEMLZ',
  },
];

/** Keep static HTML and client navigation on the same image and dimensions. */
export function getSocialImageMetadata(page: StaticPage, origin: string) {
  const image = page.socialImage ?? {
    path: '/og-image.png',
    alt: 'Hold, move, and automate digital assets without getting hacked',
    width: 1200,
    height: 630,
    type: 'image/png',
  };
  const url = origin + image.path;
  return {
    'og:image': url,
    'og:image:secure_url': url,
    'og:image:type': image.type,
    'og:image:width': String(image.width),
    'og:image:height': String(image.height),
    'og:image:alt': image.alt,
    'twitter:image': url,
    'twitter:image:alt': image.alt,
  };
}

export function getArticleStructuredData(page: StaticPage, origin: string) {
  if (!page.article) return undefined;
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: page.article.headline,
    description: page.description,
    datePublished: page.article.datePublished,
    dateModified: page.article.dateModified,
    articleSection: page.article.category,
    author: {
      '@type': 'Person',
      name: page.article.authorName,
      image: origin + page.article.authorImage,
    },
    publisher: { '@type': 'Organization', name: 'Den', url: origin },
    mainEntityOfPage: origin + page.canonicalPath,
    url: origin + page.canonicalPath,
    image: getSocialImageMetadata(page, origin)['og:image'],
    inLanguage: 'en-US',
  };
}

const menu = [...productMenu, ...securityMenu, ...solutionsMenu];

// Every content slug has one preferred URL, including slugs shared by menus.
export function getCanonicalPath(slug: string): string {
  return (
    menu.find((item) => item.href.endsWith(`/${slug}`))?.href ??
    `/product/${slug}`
  );
}

export const staticPages: StaticPage[] = [
  ...adCampaigns.map((campaign) => ({
    path: campaign.path,
    canonicalPath: campaign.path,
    title: 'Book a Personalized Wallet Platform Demo | Den',
    description:
      'See how Den helps your team manage crypto treasury, run payments, and build with secure wallets. Book a personalized demo of the Den wallet platform.',
    // Paid campaigns have a focused destination outside the main navigation.
    noIndex: true,
  })),
  {
    path: '/',
    canonicalPath: '/',
    title: 'Enterprise Crypto Wallet & Wallet-as-a-Service | Den',
    description:
      'Manage crypto treasury, stablecoin payments, and customer wallets with Den. Multi-layer security combines mobile signing, a Guardian, and onchain policies.',
  },
  {
    path: '/blog',
    canonicalPath: '/blog',
    title: 'Insights and updates from Den | Blog',
    description:
      'Practical guides and comparisons for teams building with digital assets. Explore wallet infrastructure, treasury operations, and onchain security.',
    noIndex: !articles.some(
      (article) => !article.example && article.status === 'published'
    ),
  },
  ...blogCategories.map((category) => ({
    path: category.href,
    canonicalPath: category.href,
    title: `${category.label} articles | Den Blog`,
    description: category.description,
    blogCategory: category.label,
    noIndex: !articles.some(
      (article) =>
        article.category === category.label &&
        article.status === 'published' &&
        !article.example
    ),
  })),
  ...articles.map((article) => ({
    path: `/blog/${article.slug}`,
    canonicalPath: `/blog/${article.slug}`,
    title: `${article.title} | Den`,
    description: article.description,
    ...(article.socialImage && article.imageAlt
      ? {
          socialImage: {
            path: article.socialImage,
            alt: article.imageAlt,
            width: 1200,
            height: 720,
            type: 'image/png',
          },
        }
      : {}),
    noIndex: article.example || article.status === 'draft',
    article: {
      headline: article.title,
      datePublished: article.publishedAt,
      dateModified: article.updatedAt || article.publishedAt,
      authorName: getArticleAuthor(article.category).name,
      authorImage: getArticleAuthor(article.category).image,
      category: article.category,
      example: article.example,
      ...(article.image && article.imageAlt
        ? { image: article.image, imageAlt: article.imageAlt }
        : {}),
    },
  })),
  // Preserve every URL supported by the existing three slug routes, including
  // older /product/security-feature links. Prefer the current nav URL in SEO.
  ...['product', 'security', 'solutions'].flatMap((section) =>
    Object.entries(pageContent).map(([slug, content]) => {
      const path = `/${section}/${slug}`;
      const canonicalPath = getCanonicalPath(slug);
      return {
        path,
        canonicalPath,
        title: content.seoTitle,
        description: content.seoDescription,
      };
    })
  ),
  {
    path: '/terms',
    canonicalPath: '/terms',
    title: 'Terms of Use | Den',
    description: 'Terms of use for the Den MLS Wallet and other Den offerings.',
  },
  {
    path: '/privacy',
    canonicalPath: '/privacy',
    title: 'Privacy Policy | Den',
    description: 'How Den collects, uses, and protects your information.',
  },
];

export function getPageMetadata(pathname: string): StaticPage {
  const path = pathname.replace(/\/+$/, '') || '/';
  return (
    staticPages.find((page) => page.path === path) ?? {
      path,
      canonicalPath: path,
      title: 'Page not found | Den',
      description: 'The page you requested could not be found.',
      noIndex: true,
    }
  );
}
